import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { Add, CloudUpload, DeleteOutline, FactCheck } from '@mui/icons-material';
import { API_BASE } from '../api';
import { cleanCpfCnpj, digitsOnly, formatCnpj, formatCpf, isValidCpf } from '../utils/masks';

const fetchWithRetry = async (url, options, retries = 3, delayMs = 1200) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    let response;
    try {
      response = await fetch(url, options);
    } catch (err) {
      if (attempt === retries) throw err;
      await new Promise((r) => setTimeout(r, delayMs));
      continue;
    }
    // retry on gateway/server errors (502, 503, 504) so Puma restarts are transparent
    if (response.status >= 502 && response.status <= 504 && attempt < retries) {
      await new Promise((r) => setTimeout(r, delayMs));
      continue;
    }
    return response;
  }
};

const steps = [
  'Solicitação do serviço',
  'Cadastro da empresa',
  'Funcionários',
  'Documentação',
  'Revisão e envio',
];

const DOCUMENTO_FUNCIONARIO_OPTIONS = [
  { value: 'Ficha de Registro', label: 'Ficha de Registro' },
  { value: 'Ordem de Serviço', label: 'Ordem de Serviço' },
  { value: 'ASO', label: 'ASO' },
  { value: 'Ficha de EPIs', label: 'Ficha de EPIs' },
];

const TREINAMENTO_FUNCIONARIO_OPTIONS = [
  { value: 'NR 06', label: 'NR 06 – Equipamentos de Proteção Individual (EPI)' },
  { value: 'NR 10', label: 'NR 10 – Segurança em Instalações e Serviços em Eletricidade' },
  { value: 'NR 12', label: 'NR 12 – Segurança no Trabalho em Máquinas e Equipamentos' },
  {
    value: 'NR 18',
    label: 'NR 18 – Condições de Segurança e Saúde no Trabalho na Indústria da Construção',
  },
  {
    value: 'NR 34',
    label:
      'NR 34 – Condições e Meio Ambiente de Trabalho na Indústria da Construção, Reparação e Desmonte Naval',
  },
  { value: 'NR 35', label: 'NR 35 – Trabalho em Altura' },
];

const DOCUMENTOS_EMPRESA_OBRIGATORIOS = [
  { key: 'pgr', label: 'PGR', descricao: 'Programa de Gerenciamento de Riscos' },
  {
    key: 'pcmso',
    label: 'PCMSO',
    descricao: 'Programa de Controle Médico de Saúde Ocupacional',
  },
];

const TREINAMENTO_VALIDADE_PADRAO_ANOS = {
  'NR 10': 2,
  'NR 35': 2,
};

const VENCIMENTO_ALERTA_DIAS = 30;

const emptyForm = {
  unidade_setor_solicitante: '',
  solicitante_nome: '',
  solicitante_email: '',
  descricao_servico: '',
  local_prestacao: '',
  razao_social: '',
  nome_fantasia: '',
  cnpj: '',
  responsavel_legal: '',
  telefone_contato: '',
  email_contato: '',
  endereco: '',
  atividade_executada: '',
};

const statusLabels = {
  enviado: 'Enviado',
  em_analise: 'Em análise',
  pendente_correcao: 'Pendente de correção',
  aprovado: 'Aprovado',
  integracao_agendada: 'Integração agendada',
  liberado: 'Liberado para atividades',
};

const defaultCnpjLookupMessage =
  'Ao completar o CNPJ, os dados da empresa serão buscados automaticamente.';

const createLocalId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const createUploadKey = () => `upl_${createLocalId()}`;

const addYearsToDate = (value, years) => {
  if (!value || !years) return '';

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return '';

  const nextDate = new Date(year, month - 1, day);
  nextDate.setFullYear(nextDate.getFullYear() + years);

  const nextYear = nextDate.getFullYear();
  const nextMonth = String(nextDate.getMonth() + 1).padStart(2, '0');
  const nextDay = String(nextDate.getDate()).padStart(2, '0');
  return `${nextYear}-${nextMonth}-${nextDay}`;
};

const getExpirationStatusMeta = (value) => {
  if (!value) {
    return { label: 'Sem vencimento informado', color: 'text.secondary' };
  }

  const reference = new Date(`${value}T00:00:00`);
  if (Number.isNaN(reference.getTime())) {
    return { label: 'Vencimento inválido', color: 'warning.main' };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffInDays = Math.round((reference.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays < 0) {
    return { label: `Vencido há ${Math.abs(diffInDays)} dia(s)`, color: 'error.main' };
  }

  if (diffInDays <= VENCIMENTO_ALERTA_DIAS) {
    return { label: `Vence em ${diffInDays} dia(s)`, color: 'warning.main' };
  }

  return { label: 'Em dia', color: 'success.main' };
};

const sanitizeFileMeta = (fileMeta) => {
  if (!fileMeta || typeof fileMeta !== 'object') return null;

  const id = String(fileMeta.id || '').trim();
  const originalName = String(fileMeta.original_name || '').trim();
  const relativePath = String(fileMeta.relative_path || '').trim();
  const storedName = String(fileMeta.stored_name || '').trim();

  if (!id && !originalName && !relativePath && !storedName) {
    return null;
  }

  return {
    id: id || undefined,
    original_name: originalName || storedName || 'Arquivo enviado',
    relative_path: relativePath || undefined,
    stored_name: storedName || undefined,
    uploaded_at: fileMeta.uploaded_at || undefined,
    size: typeof fileMeta.size === 'number' ? fileMeta.size : undefined,
    categoria: fileMeta.categoria || undefined,
  };
};

const createEmptyDocumentoFuncionario = (tipoDoc) => ({
  id: createLocalId(),
  tipo_doc: tipoDoc,
  data_vencimento: '',
  arquivo: null,
  arquivo_file: null,
  upload_key: '',
});

const createBaseDocumentosFuncionario = () =>
  DOCUMENTO_FUNCIONARIO_OPTIONS.map((option) => createEmptyDocumentoFuncionario(option.value));

const mergeDocumentosFuncionario = (documentos = []) => {
  const base = new Map(
    DOCUMENTO_FUNCIONARIO_OPTIONS.map((option) => [
      option.value,
      createEmptyDocumentoFuncionario(option.value),
    ])
  );

  documentos.forEach((item) => {
    const tipoDoc = String(item?.tipo_doc || '').trim();
    if (!base.has(tipoDoc)) return;

    const current = base.get(tipoDoc);
    base.set(tipoDoc, {
      ...current,
      data_vencimento: item?.data_vencimento || '',
      arquivo: sanitizeFileMeta(item?.arquivo || item),
    });
  });

  return DOCUMENTO_FUNCIONARIO_OPTIONS.map((option) => base.get(option.value));
};

const normalizeTreinamentoFuncionario = (item = {}) => ({
  id: item.id || createLocalId(),
  treinamento_nome: item.treinamento_nome || '',
  data_realizacao: item.data_realizacao || '',
  data_vencimento: item.data_vencimento || '',
  observacoes: item.observacoes || '',
  arquivo: sanitizeFileMeta(item.arquivo || item),
  arquivo_file: null,
  upload_key: '',
});

const sortTreinamentosFuncionario = (treinamentos = []) =>
  [...treinamentos].sort((left, right) => {
    const treinamentoLeft = String(left?.treinamento_nome || '');
    const treinamentoRight = String(right?.treinamento_nome || '');
    const comparison = treinamentoLeft.localeCompare(treinamentoRight, 'pt-BR', {
      sensitivity: 'base',
    });
    if (comparison !== 0) return comparison;

    return String(left?.data_realizacao || '').localeCompare(String(right?.data_realizacao || ''));
  });

const createEmptyTreinamentoFuncionario = () => ({
  id: createLocalId(),
  treinamento_nome: '',
  data_realizacao: '',
  data_vencimento: '',
  observacoes: '',
  arquivo: null,
  arquivo_file: null,
  upload_key: '',
});

const createEmptyFuncionario = () => ({
  local_id: createLocalId(),
  nome: '',
  cpf: '',
  funcao: '',
  telefone: '',
  email: '',
  documentos: createBaseDocumentosFuncionario(),
  treinamentos: [],
});

const hydrateFuncionarios = (funcionarios = []) => {
  if (!Array.isArray(funcionarios) || funcionarios.length === 0) {
    return [createEmptyFuncionario()];
  }

  return funcionarios.map((item) => ({
    ...createEmptyFuncionario(),
    local_id: item.local_id || createLocalId(),
    nome: item?.nome || '',
    cpf: formatCpf(item?.cpf || ''),
    funcao: item?.funcao || '',
    telefone: item?.telefone || '',
    email: item?.email || '',
    documentos: mergeDocumentosFuncionario(item?.documentos || []),
    treinamentos: sortTreinamentosFuncionario(
      Array.isArray(item?.treinamentos)
        ? item.treinamentos.map((treinamento) => normalizeTreinamentoFuncionario(treinamento))
        : []
    ),
  }));
};

const getFuncionarioDisplayName = (funcionario, index) =>
  funcionario?.nome?.trim() || `Colaborador ${index + 1}`;

const getFuncionarioCpfValidationMessage = (cpfValue, funcionarioLabel) => {
  const normalizedCpf = digitsOnly(cpfValue);
  if (!normalizedCpf) {
    return `Informe o CPF de ${funcionarioLabel}.`;
  }

  if (!isValidCpf(normalizedCpf)) {
    return `Informe um CPF valido para ${funcionarioLabel}.`;
  }

  return '';
};

const buildUploadFileName = (uploadKey, fileName) => `${uploadKey}__${fileName}`;

function formatBrasilApiPhone(phoneValue = '') {
  const digits = phoneValue.toString().replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length <= 2) return digits;
  return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
}

function formatBrasilApiAddress(data = {}) {
  return [
    data.logradouro,
    data.numero,
    data.complemento,
    data.bairro,
    data.municipio ? `${data.municipio}/${data.uf}` : '',
    data.cep ? `CEP ${data.cep}` : '',
  ]
    .filter(Boolean)
    .join(', ');
}

export default function CadastroEmpreiteiraPublica() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [funcionarios, setFuncionarios] = useState([createEmptyFuncionario()]);
  const [empresaFiles, setEmpresaFiles] = useState([]);
  const [empresaPgrFile, setEmpresaPgrFile] = useState(null);
  const [empresaPcmsoFile, setEmpresaPcmsoFile] = useState(null);
  const [record, setRecord] = useState(null);
  const [loadingToken, setLoadingToken] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [cnpjLookup, setCnpjLookup] = useState({
    status: 'info',
    message: defaultCnpjLookupMessage,
  });
  const [honeypotValue, setHoneypotValue] = useState('');
  const [feedback, setFeedback] = useState({ type: 'info', message: '' });
  const lastFetchedCnpjRef = useRef('');
  const currentLookupCnpjRef = useRef('');

  const currentToken = searchParams.get('token') || '';
  const requestedStepRaw = searchParams.get('step');
  const requestedStep = Number.isInteger(Number(requestedStepRaw))
    ? Number(requestedStepRaw)
    : null;
  const empresaLookupLocked = buscandoCnpj;

  useEffect(() => {
    if (requestedStep === null) return;
    const normalizedStep = Math.max(0, Math.min(steps.length - 1, requestedStep));
    setActiveStep(normalizedStep);
  }, [requestedStep]);

  const legacyFuncionarioFiles = useMemo(
    () =>
      (record?.documentos_funcionarios || []).filter(
        (item) => !item?.categoria && !item?.tipo_doc && !item?.treinamento_nome
      ),
    [record]
  );

  const totalFuncionarioFiles = useMemo(() => {
    const nestedCount = funcionarios.reduce((total, funcionario) => {
      const totalDocumentos = funcionario.documentos.filter(
        (documento) => documento.arquivo || documento.arquivo_file
      ).length;
      const totalTreinamentos = funcionario.treinamentos.filter(
        (treinamento) => treinamento.arquivo || treinamento.arquivo_file
      ).length;
      return total + totalDocumentos + totalTreinamentos;
    }, 0);

    return nestedCount + legacyFuncionarioFiles.length;
  }, [funcionarios, legacyFuncionarioFiles]);

  const totalTreinamentosInformados = useMemo(
    () =>
      funcionarios.reduce(
        (total, funcionario) =>
          total +
          funcionario.treinamentos.filter(
            (item) =>
              item.treinamento_nome ||
              item.data_realizacao ||
              item.data_vencimento ||
              item.observacoes ||
              item.arquivo ||
              item.arquivo_file
          ).length,
        0
      ),
    [funcionarios]
  );

  const protocolText = useMemo(() => {
    if (!record?.protocolo) return '';
    return `Protocolo ${record.protocolo}`;
  }, [record]);

  useEffect(() => {
    if (!currentToken) return;

    const loadRecord = async () => {
      try {
        setLoadingToken(true);
        const response = await fetchWithRetry(`${API_BASE}/cadastro-empreiteiras/public/${currentToken}`);
        const data = await response.json();
        if (!response.ok) {
          throw new Error(
            data?.detail || data?.error || 'Não foi possível carregar o cadastro informado'
          );
        }

        setRecord(data);
        setForm({
          unidade_setor_solicitante: data.unidade_setor_solicitante || '',
          solicitante_nome: data.solicitante_nome || '',
          solicitante_email: data.solicitante_email || '',
          descricao_servico: data.descricao_servico || '',
          local_prestacao: data.local_prestacao || '',
          razao_social: data.razao_social || '',
          nome_fantasia: data.nome_fantasia || '',
          cnpj: formatCnpj(data.cnpj || ''),
          responsavel_legal: data.responsavel_legal || '',
          telefone_contato: data.telefone_contato || '',
          email_contato: data.email_contato || '',
          endereco: data.endereco || '',
          atividade_executada: data.atividade_executada || '',
        });
        setFuncionarios(hydrateFuncionarios(data.funcionarios));
        setEmpresaFiles([]);
      } catch (error) {
        setFeedback({ type: 'error', message: error.message });
      } finally {
        setLoadingToken(false);
      }
    };

    loadRecord();
  }, [currentToken]);

  const handleBuscarCnpj = async (cnpjValue, force = false) => {
    const normalizedCnpj = cleanCpfCnpj(cnpjValue);

    if (normalizedCnpj.length !== 14) {
      if (force) {
        setCnpjLookup({
          status: 'warning',
          message: 'Informe um CNPJ válido com 14 dígitos para buscar.',
        });
      }
      return;
    }

    if (
      !force &&
      (lastFetchedCnpjRef.current === normalizedCnpj ||
        currentLookupCnpjRef.current === normalizedCnpj)
    ) {
      return;
    }

    currentLookupCnpjRef.current = normalizedCnpj;
    setBuscandoCnpj(true);
    setCnpjLookup({ status: 'info', message: 'Buscando dados da empresa pelo CNPJ...' });

    try {
      const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${normalizedCnpj}`);
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.message || 'CNPJ não encontrado');
      }

      const data = await response.json();
      const formattedPhone = formatBrasilApiPhone(data.ddd_telefone_1);
      const formattedAddress = formatBrasilApiAddress(data);

      setForm((prev) => {
        if (cleanCpfCnpj(prev.cnpj) !== normalizedCnpj) {
          return prev;
        }

        return {
          ...prev,
          cnpj: formatCnpj(normalizedCnpj),
          razao_social: data.razao_social || prev.razao_social,
          nome_fantasia: data.nome_fantasia || prev.nome_fantasia,
          telefone_contato: formattedPhone || prev.telefone_contato,
          email_contato: data.email && data.email !== 'null' ? data.email : prev.email_contato,
          endereco: formattedAddress || prev.endereco,
        };
      });

      lastFetchedCnpjRef.current = normalizedCnpj;
      setCnpjLookup({
        status: 'success',
        message: 'Dados da empresa preenchidos automaticamente pelo CNPJ.',
      });
    } catch (error) {
      setCnpjLookup({
        status: 'warning',
        message: `Não foi possível localizar o CNPJ automaticamente: ${error.message}`,
      });
    } finally {
      if (currentLookupCnpjRef.current === normalizedCnpj) {
        currentLookupCnpjRef.current = '';
      }
      setBuscandoCnpj(false);
    }
  };

  const handleCnpjChange = (value) => {
    const formattedValue = formatCnpj(value);
    const normalizedCnpj = cleanCpfCnpj(formattedValue);

    setForm((prev) => ({ ...prev, cnpj: formattedValue }));

    if (normalizedCnpj.length < 14) {
      lastFetchedCnpjRef.current = '';
      setCnpjLookup({ status: 'info', message: defaultCnpjLookupMessage });
      return;
    }

    void handleBuscarCnpj(formattedValue);
  };

  const updateFuncionario = (index, field, value) => {
    setFuncionarios((prev) =>
      prev.map((item, currentIndex) =>
        currentIndex === index
          ? {
              ...item,
              [field]: field === 'cpf' ? formatCpf(value) : value,
            }
          : item
      )
    );
  };

  const updateDocumentoFuncionario = (funcionarioIndex, tipoDoc, updater) => {
    setFuncionarios((prev) =>
      prev.map((funcionario, currentIndex) => {
        if (currentIndex !== funcionarioIndex) return funcionario;

        return {
          ...funcionario,
          documentos: funcionario.documentos.map((documento) => {
            if (documento.tipo_doc !== tipoDoc) return documento;
            return typeof updater === 'function'
              ? updater(documento)
              : { ...documento, ...updater };
          }),
        };
      })
    );
  };

  const setDocumentoFuncionarioFile = (funcionarioIndex, tipoDoc, file) => {
    if (!file) return;

    updateDocumentoFuncionario(funcionarioIndex, tipoDoc, (documento) => ({
      ...documento,
      arquivo_file: file,
      upload_key: createUploadKey(),
    }));
  };

  const setEmpresaDocumentoObrigatorio = (tipo, file) => {
    if (!file) return;

    if (tipo === 'pgr') {
      setEmpresaPgrFile(file);
      return;
    }

    if (tipo === 'pcmso') {
      setEmpresaPcmsoFile(file);
    }
  };

  const addTreinamentoFuncionario = (funcionarioIndex) => {
    setFuncionarios((prev) =>
      prev.map((funcionario, currentIndex) =>
        currentIndex === funcionarioIndex
          ? {
              ...funcionario,
              treinamentos: [...funcionario.treinamentos, createEmptyTreinamentoFuncionario()],
            }
          : funcionario
      )
    );
  };

  const updateTreinamentoFuncionario = (funcionarioIndex, treinamentoId, field, value) => {
    setFuncionarios((prev) =>
      prev.map((funcionario, currentIndex) => {
        if (currentIndex !== funcionarioIndex) return funcionario;

        return {
          ...funcionario,
          treinamentos: funcionario.treinamentos.map((treinamento) => {
            if (treinamento.id !== treinamentoId) return treinamento;

            const next = { ...treinamento, [field]: value };
            const validityYears = TREINAMENTO_VALIDADE_PADRAO_ANOS[next.treinamento_nome];

            if (
              (field === 'treinamento_nome' || field === 'data_realizacao') &&
              validityYears &&
              next.data_realizacao
            ) {
              next.data_vencimento = addYearsToDate(next.data_realizacao, validityYears);
            }

            return next;
          }),
        };
      })
    );
  };

  const setTreinamentoFuncionarioFile = (funcionarioIndex, treinamentoId, file) => {
    if (!file) return;

    setFuncionarios((prev) =>
      prev.map((funcionario, currentIndex) => {
        if (currentIndex !== funcionarioIndex) return funcionario;

        return {
          ...funcionario,
          treinamentos: funcionario.treinamentos.map((treinamento) =>
            treinamento.id === treinamentoId
              ? {
                  ...treinamento,
                  arquivo_file: file,
                  upload_key: createUploadKey(),
                }
              : treinamento
          ),
        };
      })
    );
  };

  const removeTreinamentoFuncionario = (funcionarioIndex, treinamentoId) => {
    setFuncionarios((prev) =>
      prev.map((funcionario, currentIndex) =>
        currentIndex === funcionarioIndex
          ? {
              ...funcionario,
              treinamentos: funcionario.treinamentos.filter(
                (treinamento) => treinamento.id !== treinamentoId
              ),
            }
          : funcionario
      )
    );
  };

  const addFuncionario = () => {
    setFuncionarios((prev) => [...prev, createEmptyFuncionario()]);
  };

  const removeFuncionario = (index) => {
    setFuncionarios((prev) =>
      prev.length === 1 ? prev : prev.filter((_, currentIndex) => currentIndex !== index)
    );
  };

  const validateComplianceStep = () => {
    for (let funcionarioIndex = 0; funcionarioIndex < funcionarios.length; funcionarioIndex += 1) {
      const funcionario = funcionarios[funcionarioIndex];
      const funcionarioLabel = getFuncionarioDisplayName(funcionario, funcionarioIndex);
      const asoDocumento = funcionario.documentos.find((documento) => documento.tipo_doc === 'ASO');

      if (
        asoDocumento &&
        (asoDocumento.arquivo || asoDocumento.arquivo_file || asoDocumento.data_vencimento) &&
        !asoDocumento.data_vencimento
      ) {
        return `Informe a data de vencimento do ASO de ${funcionarioLabel}.`;
      }

      for (
        let treinamentoIndex = 0;
        treinamentoIndex < funcionario.treinamentos.length;
        treinamentoIndex += 1
      ) {
        const treinamento = funcionario.treinamentos[treinamentoIndex];
        const hasTrainingData = Boolean(
          treinamento.treinamento_nome ||
            treinamento.data_realizacao ||
            treinamento.data_vencimento ||
            treinamento.observacoes ||
            treinamento.arquivo ||
            treinamento.arquivo_file
        );

        if (!hasTrainingData) continue;

        if (!treinamento.treinamento_nome) {
          return `Selecione o treinamento informado para ${funcionarioLabel}.`;
        }

        if (!treinamento.data_realizacao) {
          return `Informe a data de realização do treinamento ${treinamento.treinamento_nome} de ${funcionarioLabel}.`;
        }

        if (
          treinamento.data_vencimento &&
          treinamento.data_realizacao &&
          treinamento.data_vencimento < treinamento.data_realizacao
        ) {
          return `A data de vencimento do treinamento ${treinamento.treinamento_nome} de ${funcionarioLabel} não pode ser anterior à realização.`;
        }
      }
    }

    return '';
  };

  const getStepValidationMessage = () => {
    if (activeStep === 0) {
      if (!form.unidade_setor_solicitante.trim() || !form.descricao_servico.trim()) {
        return 'Preencha a unidade solicitante e a descrição do serviço antes de continuar.';
      }
      return '';
    }

    if (activeStep === 1) {
      if (
        !form.razao_social.trim() ||
        !form.cnpj.trim() ||
        !form.responsavel_legal.trim() ||
        !form.atividade_executada.trim()
      ) {
        return 'Preencha razão social, CNPJ, responsável legal e atividade executada antes de continuar.';
      }
      return '';
    }

    if (activeStep === 2) {
      const invalidFuncionario = funcionarios.find(
        (funcionario) =>
          !funcionario.nome?.trim() || !funcionario.funcao?.trim() || !digitsOnly(funcionario.cpf)
      );

      if (invalidFuncionario) {
        return 'Cada colaborador precisa ter nome, CPF e funcao informados.';
      }

      for (let index = 0; index < funcionarios.length; index += 1) {
        const funcionario = funcionarios[index];
        const cpfValidationMessage = getFuncionarioCpfValidationMessage(
          funcionario.cpf,
          getFuncionarioDisplayName(funcionario, index)
        );
        if (cpfValidationMessage) {
          return cpfValidationMessage;
        }
      }

      return '';
    }

    if (activeStep === 3) {
      return validateComplianceStep();
    }

    return '';
  };

  const buildPublicDownloadUrl = (documentId) => {
    if (!currentToken || !documentId) return '';
    return `${API_BASE}/cadastro-empreiteiras/public/${currentToken}/documentos/${documentId}/download`;
  };

  const serializeFuncionariosForPayload = () =>
    funcionarios.map((funcionario) => ({
      nome: funcionario.nome,
      cpf: digitsOnly(funcionario.cpf),
      funcao: funcionario.funcao,
      telefone: funcionario.telefone,
      email: funcionario.email,
      documentos: funcionario.documentos.map((documento) => ({
        tipo_doc: documento.tipo_doc,
        data_vencimento: documento.data_vencimento || '',
        upload_key: documento.upload_key || '',
        arquivo: sanitizeFileMeta(documento.arquivo),
      })),
      treinamentos: funcionario.treinamentos.map((treinamento) => ({
        treinamento_nome: treinamento.treinamento_nome,
        data_realizacao: treinamento.data_realizacao || '',
        data_vencimento: treinamento.data_vencimento || '',
        observacoes: treinamento.observacoes || '',
        upload_key: treinamento.upload_key || '',
        arquivo: sanitizeFileMeta(treinamento.arquivo),
      })),
    }));

  const appendFuncionarioUploadsToFormData = (formData) => {
    funcionarios.forEach((funcionario) => {
      funcionario.documentos.forEach((documento) => {
        if (documento.arquivo_file && documento.upload_key) {
          formData.append(
            'funcionarios_documentos[]',
            documento.arquivo_file,
            buildUploadFileName(documento.upload_key, documento.arquivo_file.name)
          );
        }
      });

      funcionario.treinamentos.forEach((treinamento) => {
        if (treinamento.arquivo_file && treinamento.upload_key) {
          formData.append(
            'funcionarios_treinamentos[]',
            treinamento.arquivo_file,
            buildUploadFileName(treinamento.upload_key, treinamento.arquivo_file.name)
          );
        }
      });
    });
  };

  const handleAdvanceStep = () => {
    const validationMessage = getStepValidationMessage();
    if (validationMessage) {
      setFeedback({ type: 'warning', message: validationMessage });
      return;
    }

    setFeedback({ type: 'info', message: '' });
    setActiveStep((prev) => prev + 1);
  };

  const submitCadastro = async () => {
    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append(
        'payload_json',
        JSON.stringify({
          ...form,
          cnpj: cleanCpfCnpj(form.cnpj),
          funcionarios: serializeFuncionariosForPayload(),
        })
      );
      if (currentToken) {
        formData.append('public_token', currentToken);
      }
      formData.append('company_website', honeypotValue);
      if (empresaPgrFile) {
        formData.append('empresa_documentos[]', empresaPgrFile);
      }
      if (empresaPcmsoFile) {
        formData.append('empresa_documentos[]', empresaPcmsoFile);
      }
      empresaFiles.forEach((file) => formData.append('empresa_documentos[]', file));
      appendFuncionarioUploadsToFormData(formData);

      const response = await fetch(`${API_BASE}/cadastro-empreiteiras/public`, {
        method: 'POST',
        body: formData,
      });
      if (response.status === 413) {
        throw new Error(
          'Os arquivos anexados são muito grandes. Reduza o tamanho ou a quantidade de documentos e tente novamente.'
        );
      }
      const data = await response.json().catch(() => {
        throw new Error('Erro inesperado do servidor. Tente novamente em instantes.');
      });
      if (!response.ok) {
        throw new Error(data?.detail || data?.error || 'Não foi possível enviar o cadastro');
      }

      setRecord(data);
      if (data.public_token) {
        setSearchParams({ token: data.public_token });
      }
      setFuncionarios(hydrateFuncionarios(data.funcionarios));
      setEmpresaFiles([]);
      setEmpresaPgrFile(null);
      setEmpresaPcmsoFile(null);
      setFeedback({
        type: 'success',
        message: currentToken
          ? 'Cadastro atualizado com sucesso.'
          : `Cadastro enviado com sucesso. Guarde o protocolo ${data.protocolo} para acompanhar e reenviar documentos, se necessário.`,
      });
      setActiveStep(4);
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: { xs: 3, md: 5 },
        background: 'linear-gradient(180deg, #f4f8ff 0%, #fff8f1 100%)',
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Paper
            sx={{
              borderRadius: 5,
              p: { xs: 2.5, md: 4 },
              boxShadow: '0 24px 50px rgba(15,23,42,0.08)',
            }}
          >
            <Stack spacing={2}>
              <Chip
                label="Portal de Cadastro de Empreiteira"
                sx={{ width: 'fit-content', fontWeight: 700 }}
              />
              <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
                Envie o cadastro da sua empresa ao Mercadão Atacadista
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Preencha os dados da empresa, cadastre os colaboradores que irão atuar no Mercadão
                Atacadista e anexe a documentação obrigatória. Se houver pendências, use o mesmo
                link com protocolo para corrigir e reenviar os dados.
              </Typography>
              {record ? (
                <Alert severity={record.status === 'pendente_correcao' ? 'warning' : 'info'}>
                  {protocolText ? `${protocolText} • ` : ''}
                  {statusLabels[record.status] || record.status}
                  {record.pendencia_motivo ? ` • ${record.pendencia_motivo}` : ''}
                </Alert>
              ) : null}
              {feedback.message ? <Alert severity={feedback.type}>{feedback.message}</Alert> : null}
            </Stack>
          </Paper>

          <Paper sx={{ borderRadius: 5, p: { xs: 2, md: 3 } }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, overflowX: 'auto' }}>
              {steps.map((step) => (
                <Step key={step}>
                  <StepLabel>{step}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Stack spacing={3}>
              {loadingToken ? (
                <Alert severity="info">Carregando cadastro existente...</Alert>
              ) : null}

              {activeStep === 0 ? (
                <Stack spacing={2}>
                  <TextField
                    label="Unidade / setor solicitante"
                    value={form.unidade_setor_solicitante}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, unidade_setor_solicitante: e.target.value }))
                    }
                  />
                  <TextField
                    label="Nome do solicitante"
                    value={form.solicitante_nome}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, solicitante_nome: e.target.value }))
                    }
                  />
                  <TextField
                    label="E-mail do solicitante"
                    value={form.solicitante_email}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, solicitante_email: e.target.value }))
                    }
                  />
                  <TextField
                    label="Descrição da prestação de serviço"
                    value={form.descricao_servico}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, descricao_servico: e.target.value }))
                    }
                    multiline
                    minRows={3}
                  />
                  <TextField
                    label="Local de prestação"
                    value={form.local_prestacao}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, local_prestacao: e.target.value }))
                    }
                  />
                </Stack>
              ) : null}

              {activeStep === 1 ? (
                <Stack spacing={2}>
                  {empresaLookupLocked ? (
                    <Alert severity="info">
                      Consulta em andamento. Os campos preenchidos automaticamente pelo CNPJ ficam
                      temporariamente bloqueados até a busca terminar.
                    </Alert>
                  ) : null}
                  <TextField
                    label="Razão social"
                    value={form.razao_social}
                    onChange={(e) => setForm((prev) => ({ ...prev, razao_social: e.target.value }))}
                    disabled={empresaLookupLocked}
                  />
                  <TextField
                    label="Nome fantasia"
                    value={form.nome_fantasia}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, nome_fantasia: e.target.value }))
                    }
                    disabled={empresaLookupLocked}
                  />
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ sm: 'flex-start' }}
                  >
                    <TextField
                      label="CNPJ"
                      fullWidth
                      value={form.cnpj}
                      onChange={(e) => handleCnpjChange(e.target.value)}
                      helperText={cnpjLookup.message}
                      error={cnpjLookup.status === 'warning'}
                      disabled={empresaLookupLocked}
                    />
                    <Button
                      variant="outlined"
                      sx={{ minWidth: { sm: 110 }, height: 56 }}
                      disabled={buscandoCnpj || cleanCpfCnpj(form.cnpj).length !== 14}
                      onClick={() => handleBuscarCnpj(form.cnpj, true)}
                    >
                      {buscandoCnpj ? 'Buscando...' : 'Buscar'}
                    </Button>
                  </Stack>
                  <TextField
                    label="Responsável legal"
                    value={form.responsavel_legal}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, responsavel_legal: e.target.value }))
                    }
                  />
                  <TextField
                    label="Telefone de contato"
                    value={form.telefone_contato}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, telefone_contato: e.target.value }))
                    }
                    disabled={empresaLookupLocked}
                  />
                  <TextField
                    label="E-mail de contato"
                    value={form.email_contato}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, email_contato: e.target.value }))
                    }
                    disabled={empresaLookupLocked}
                  />
                  <TextField
                    label="Endereço"
                    value={form.endereco}
                    onChange={(e) => setForm((prev) => ({ ...prev, endereco: e.target.value }))}
                    multiline
                    minRows={2}
                    disabled={empresaLookupLocked}
                  />
                  <TextField
                    label="Atividade a ser executada"
                    value={form.atividade_executada}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, atividade_executada: e.target.value }))
                    }
                    multiline
                    minRows={3}
                  />
                </Stack>
              ) : null}

              {activeStep === 2 ? (
                <Stack spacing={2}>
                  {funcionarios.map((funcionario, index) => (
                    <Paper
                      key={funcionario.local_id}
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 3 }}
                    >
                      <Stack spacing={1.5}>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          justifyContent="space-between"
                          alignItems={{ md: 'center' }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Colaborador {index + 1}
                          </Typography>
                          <Button
                            color="error"
                            startIcon={<DeleteOutline />}
                            onClick={() => removeFuncionario(index)}
                            disabled={funcionarios.length === 1}
                          >
                            Remover
                          </Button>
                        </Stack>
                        <TextField
                          label="Nome"
                          value={funcionario.nome}
                          onChange={(e) => updateFuncionario(index, 'nome', e.target.value)}
                        />
                        <TextField
                          label="CPF"
                          value={funcionario.cpf}
                          onChange={(e) => updateFuncionario(index, 'cpf', e.target.value)}
                          required
                          inputProps={{ inputMode: 'numeric' }}
                          error={Boolean(funcionario.cpf) && !isValidCpf(funcionario.cpf)}
                          helperText={
                            funcionario.cpf && !isValidCpf(funcionario.cpf)
                              ? 'Informe um CPF valido.'
                              : 'CPF obrigatorio. O registro do colaborador fica vinculado a este CPF.'
                          }
                        />
                        <TextField
                          label="Função"
                          value={funcionario.funcao}
                          onChange={(e) => updateFuncionario(index, 'funcao', e.target.value)}
                        />
                      </Stack>
                    </Paper>
                  ))}
                  <Button startIcon={<Add />} variant="outlined" onClick={addFuncionario}>
                    Adicionar colaborador
                  </Button>
                </Stack>
              ) : null}

              {activeStep === 3 ? (
                <Stack spacing={2}>
                  <Alert severity="info">
                    Os anexos dos colaboradores seguem a ordem padrão Ficha de Registro, Ordem de
                    Serviço, ASO e Ficha de EPIs. O ASO precisa trazer vencimento, e os treinamentos
                    podem ser controlados com datas e certificados neste mesmo fluxo.
                  </Alert>

                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Documentos da empresa
                      </Typography>
                      <Alert severity="info">
                        Envie os dois programas obrigatórios separadamente: PGR e PCMSO. Outros
                        documentos complementares da empresa também podem ser anexados no mesmo
                        envio.
                      </Alert>
                      <Grid container spacing={2}>
                        {DOCUMENTOS_EMPRESA_OBRIGATORIOS.map((documento) => {
                          const currentFile =
                            documento.key === 'pgr' ? empresaPgrFile : empresaPcmsoFile;

                          return (
                            <Grid item xs={12} md={6} key={documento.key}>
                              <Paper
                                variant="outlined"
                                sx={{ p: 2, borderRadius: 2, height: '100%' }}
                              >
                                <Stack spacing={1}>
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                      {documento.label}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {documento.descricao}
                                    </Typography>
                                  </Box>
                                  <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<CloudUpload />}
                                  >
                                    {currentFile ? 'Trocar arquivo' : 'Selecionar arquivo'}
                                    <input
                                      hidden
                                      type="file"
                                      onChange={(e) =>
                                        setEmpresaDocumentoObrigatorio(
                                          documento.key,
                                          e.target.files?.[0]
                                        )
                                      }
                                    />
                                  </Button>
                                  <Typography variant="body2" color="text.secondary">
                                    {currentFile
                                      ? `Arquivo selecionado: ${currentFile.name}`
                                      : 'Nenhum arquivo selecionado.'}
                                  </Typography>
                                </Stack>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                      <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
                        Selecionar arquivos da empresa
                        <input
                          hidden
                          multiple
                          type="file"
                          onChange={(e) => setEmpresaFiles(Array.from(e.target.files || []))}
                        />
                      </Button>
                      {record?.documentos_empresa?.length ? (
                        <Typography variant="body2" color="text.secondary">
                          Arquivos já enviados:{' '}
                          {record.documentos_empresa.map((item) => item.original_name).join(', ')}
                        </Typography>
                      ) : null}
                      {empresaFiles.length ? (
                        <Typography variant="body2">
                          Novos arquivos: {empresaFiles.map((item) => item.name).join(', ')}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Paper>

                  {legacyFuncionarioFiles.length ? (
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                      <Stack spacing={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Arquivos antigos sem classificação
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Estes anexos vieram de versões anteriores do portal e continuam
                          preservados no cadastro atual.
                        </Typography>
                        {legacyFuncionarioFiles.map((item) => (
                          <Typography key={item.id || item.original_name} variant="body2">
                            {item.original_name}
                          </Typography>
                        ))}
                      </Stack>
                    </Paper>
                  ) : null}

                  {funcionarios.map((funcionario, funcionarioIndex) => (
                    <Paper
                      key={`compliance-${funcionario.local_id}`}
                      variant="outlined"
                      sx={{ p: 2.5, borderRadius: 3 }}
                    >
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {getFuncionarioDisplayName(funcionario, funcionarioIndex)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            CPF: {formatCpf(funcionario.cpf) || '-'} • Funcao:{' '}
                            {funcionario.funcao || '-'}
                          </Typography>
                        </Box>

                        <Stack spacing={1.2}>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            Documentos obrigatórios
                          </Typography>
                          {funcionario.documentos.map((documento) => {
                            const expirationMeta = getExpirationStatusMeta(
                              documento.data_vencimento
                            );
                            const currentFileUrl = buildPublicDownloadUrl(documento.arquivo?.id);

                            return (
                              <Paper
                                key={`${funcionario.local_id}-${documento.tipo_doc}`}
                                variant="outlined"
                                sx={{ p: 1.5, borderRadius: 2 }}
                              >
                                <Stack spacing={1.2}>
                                  <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    justifyContent="space-between"
                                    spacing={1.5}
                                  >
                                    <Box>
                                      <Typography sx={{ fontWeight: 700 }}>
                                        {documento.tipo_doc}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {documento.tipo_doc === 'ASO'
                                          ? 'Anexe o ASO e informe a data de vencimento para controle preventivo.'
                                          : 'Anexe o arquivo correspondente deste colaborador.'}
                                      </Typography>
                                    </Box>
                                    <Button
                                      variant="outlined"
                                      component="label"
                                      startIcon={<CloudUpload />}
                                    >
                                      {documento.arquivo || documento.arquivo_file
                                        ? 'Trocar arquivo'
                                        : 'Selecionar arquivo'}
                                      <input
                                        hidden
                                        type="file"
                                        onChange={(e) =>
                                          setDocumentoFuncionarioFile(
                                            funcionarioIndex,
                                            documento.tipo_doc,
                                            e.target.files?.[0]
                                          )
                                        }
                                      />
                                    </Button>
                                  </Stack>

                                  {documento.tipo_doc === 'ASO' ? (
                                    <TextField
                                      label="Vencimento do ASO"
                                      type="date"
                                      InputLabelProps={{ shrink: true }}
                                      value={documento.data_vencimento}
                                      onChange={(e) =>
                                        updateDocumentoFuncionario(
                                          funcionarioIndex,
                                          documento.tipo_doc,
                                          { data_vencimento: e.target.value }
                                        )
                                      }
                                      helperText={
                                        documento.data_vencimento
                                          ? expirationMeta.label
                                          : 'Obrigatório quando houver ASO enviado.'
                                      }
                                    />
                                  ) : null}

                                  {documento.arquivo_file ? (
                                    <Typography variant="body2">
                                      Novo arquivo: {documento.arquivo_file.name}
                                    </Typography>
                                  ) : documento.arquivo?.id ? (
                                    <Stack
                                      direction={{ xs: 'column', sm: 'row' }}
                                      spacing={1.2}
                                      alignItems={{ sm: 'center' }}
                                    >
                                      <Typography variant="body2">
                                        Arquivo atual: {documento.arquivo.original_name}
                                      </Typography>
                                      <Button
                                        size="small"
                                        variant="text"
                                        onClick={() =>
                                          window.open(
                                            currentFileUrl,
                                            '_blank',
                                            'noopener,noreferrer'
                                          )
                                        }
                                      >
                                        Baixar atual
                                      </Button>
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      Nenhum arquivo selecionado até o momento.
                                    </Typography>
                                  )}

                                  {documento.tipo_doc === 'ASO' && documento.data_vencimento ? (
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, color: expirationMeta.color }}
                                    >
                                      {expirationMeta.label}
                                    </Typography>
                                  ) : null}
                                </Stack>
                              </Paper>
                            );
                          })}
                        </Stack>

                        <Divider />

                        <Stack spacing={1.2}>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            Controle de treinamentos
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Registre data de realização, vencimento e, se houver, o certificado do
                            treinamento do colaborador.
                          </Typography>

                          {funcionario.treinamentos.map((treinamento) => {
                            const expirationMeta = getExpirationStatusMeta(
                              treinamento.data_vencimento
                            );
                            const currentFileUrl = buildPublicDownloadUrl(treinamento.arquivo?.id);
                            const defaultHelper = treinamento.data_vencimento
                              ? expirationMeta.label
                              : TREINAMENTO_VALIDADE_PADRAO_ANOS[treinamento.treinamento_nome]
                                ? `Vencimento sugerido automaticamente para ${TREINAMENTO_VALIDADE_PADRAO_ANOS[treinamento.treinamento_nome]} ano(s).`
                                : 'Informe o vencimento quando o treinamento exigir reciclagem.';

                            return (
                              <Paper
                                key={treinamento.id}
                                variant="outlined"
                                sx={{ p: 1.5, borderRadius: 2 }}
                              >
                                <Stack spacing={1.2}>
                                  <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={1.2}
                                    alignItems={{ md: 'flex-start' }}
                                  >
                                    <TextField
                                      select
                                      fullWidth
                                      label="Treinamento"
                                      value={treinamento.treinamento_nome}
                                      onChange={(e) =>
                                        updateTreinamentoFuncionario(
                                          funcionarioIndex,
                                          treinamento.id,
                                          'treinamento_nome',
                                          e.target.value
                                        )
                                      }
                                    >
                                      {TREINAMENTO_FUNCIONARIO_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                          {option.label}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                    <Button
                                      color="error"
                                      startIcon={<DeleteOutline />}
                                      onClick={() =>
                                        removeTreinamentoFuncionario(
                                          funcionarioIndex,
                                          treinamento.id
                                        )
                                      }
                                    >
                                      Remover
                                    </Button>
                                  </Stack>

                                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2}>
                                    <TextField
                                      fullWidth
                                      label="Data de realização"
                                      type="date"
                                      InputLabelProps={{ shrink: true }}
                                      value={treinamento.data_realizacao}
                                      onChange={(e) =>
                                        updateTreinamentoFuncionario(
                                          funcionarioIndex,
                                          treinamento.id,
                                          'data_realizacao',
                                          e.target.value
                                        )
                                      }
                                    />
                                    <TextField
                                      fullWidth
                                      label="Data de vencimento"
                                      type="date"
                                      InputLabelProps={{ shrink: true }}
                                      value={treinamento.data_vencimento}
                                      onChange={(e) =>
                                        updateTreinamentoFuncionario(
                                          funcionarioIndex,
                                          treinamento.id,
                                          'data_vencimento',
                                          e.target.value
                                        )
                                      }
                                      helperText={defaultHelper}
                                    />
                                  </Stack>

                                  <TextField
                                    label="Observações"
                                    value={treinamento.observacoes}
                                    onChange={(e) =>
                                      updateTreinamentoFuncionario(
                                        funcionarioIndex,
                                        treinamento.id,
                                        'observacoes',
                                        e.target.value
                                      )
                                    }
                                    multiline
                                    minRows={2}
                                  />

                                  <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    justifyContent="space-between"
                                    spacing={1.2}
                                  >
                                    <Button
                                      variant="outlined"
                                      component="label"
                                      startIcon={<CloudUpload />}
                                    >
                                      {treinamento.arquivo || treinamento.arquivo_file
                                        ? 'Trocar certificado'
                                        : 'Anexar certificado'}
                                      <input
                                        hidden
                                        type="file"
                                        onChange={(e) =>
                                          setTreinamentoFuncionarioFile(
                                            funcionarioIndex,
                                            treinamento.id,
                                            e.target.files?.[0]
                                          )
                                        }
                                      />
                                    </Button>

                                    {treinamento.arquivo_file ? (
                                      <Typography variant="body2">
                                        Novo arquivo: {treinamento.arquivo_file.name}
                                      </Typography>
                                    ) : treinamento.arquivo?.id ? (
                                      <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1.2}
                                        alignItems={{ sm: 'center' }}
                                      >
                                        <Typography variant="body2">
                                          Certificado atual: {treinamento.arquivo.original_name}
                                        </Typography>
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={() =>
                                            window.open(
                                              currentFileUrl,
                                              '_blank',
                                              'noopener,noreferrer'
                                            )
                                          }
                                        >
                                          Baixar atual
                                        </Button>
                                      </Stack>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        Sem certificado anexado.
                                      </Typography>
                                    )}
                                  </Stack>
                                </Stack>
                              </Paper>
                            );
                          })}

                          <Button
                            startIcon={<Add />}
                            variant="outlined"
                            onClick={() => addTreinamentoFuncionario(funcionarioIndex)}
                          >
                            Adicionar treinamento
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : null}

              {activeStep === 4 ? (
                <Stack spacing={2}>
                  <Alert severity="success">
                    Revise as informações antes de enviar. Após a análise interna, o Mercadão poderá
                    aprovar, pedir correções, agendar a integração de segurança e liberar o início
                    das atividades.
                  </Alert>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    <Stack spacing={1}>
                      <Typography>
                        <strong>Empresa:</strong> {form.razao_social || '-'}
                      </Typography>
                      <Typography>
                        <strong>CNPJ:</strong> {form.cnpj || '-'}
                      </Typography>
                      <Typography>
                        <strong>Responsável legal:</strong> {form.responsavel_legal || '-'}
                      </Typography>
                      <Typography>
                        <strong>Atividade:</strong> {form.atividade_executada || '-'}
                      </Typography>
                      <Typography>
                        <strong>Colaboradores cadastrados:</strong> {funcionarios.length}
                      </Typography>
                      <Typography>
                        <strong>Arquivos da empresa:</strong>{' '}
                        {record?.documentos_empresa?.length || 0} existente(s) +{' '}
                        {empresaFiles.length +
                          [empresaPgrFile, empresaPcmsoFile].filter(Boolean).length}{' '}
                        novo(s)
                      </Typography>
                      <Typography>
                        <strong>PGR:</strong> {empresaPgrFile ? empresaPgrFile.name : 'pendente'}
                      </Typography>
                      <Typography>
                        <strong>PCMSO:</strong>{' '}
                        {empresaPcmsoFile ? empresaPcmsoFile.name : 'pendente'}
                      </Typography>
                      <Typography>
                        <strong>Arquivos dos colaboradores:</strong> {totalFuncionarioFiles}
                      </Typography>
                      <Typography>
                        <strong>Treinamentos informados:</strong> {totalTreinamentosInformados}
                      </Typography>
                    </Stack>
                  </Paper>

                  <Box aria-hidden="true" sx={{ display: 'none' }}>
                    <TextField
                      name="company_website"
                      value={honeypotValue}
                      onChange={(e) => setHoneypotValue(e.target.value)}
                      tabIndex={-1}
                    />
                  </Box>
                </Stack>
              ) : null}

              <Divider />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={1.5}
              >
                <Button
                  disabled={activeStep === 0 || submitting}
                  onClick={() => setActiveStep((prev) => prev - 1)}
                >
                  Voltar
                </Button>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  {activeStep < steps.length - 1 ? (
                    <Button variant="contained" onClick={handleAdvanceStep}>
                      Avançar
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<FactCheck />}
                      onClick={submitCadastro}
                      disabled={submitting}
                    >
                      {submitting
                        ? 'Enviando...'
                        : currentToken
                          ? 'Atualizar cadastro'
                          : 'Enviar cadastro'}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}
